import React, { useContext } from 'react';
import tw from 'twin.macro';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { gaEvent, gaItemEventLabel } from 'services/ga';
import { ItemImage, ItemContent } from './';
import { RouterContext, UserContext } from '../context';
import { TextLine, Title } from '../text';
import { KudoAmount } from '../kudos';
import { strippedContent } from '../../lib/helpers';
import { PLAYLIST_PATH } from '../../data/';

export const ItemClickWrapper = ({
  id,
  itemClick,
  children,
  landscape,
  sliderItem,
  ...restProps
}) => (
  <a
    href={`/item/${id}`}
    onClick={itemClick}
    css={[
      tw`relative cursor-pointer overflow-hidden`,
      !sliderItem && tw`rounded-lg`,
      !landscape && tw`flex items-center`,
      landscape &&
        tw`border-b border-icons border-opacity-20 pb-2 rounded-b-none sm:border-0 sm:rounded-lg sm:pb-0`,
    ]}
    className='group item-transition'
    {...restProps}
  >
    {children}
  </a>
);

export const SliderItem = ({ item }) => {
  return (
    <div tw='w-full relative aspect-w-16 aspect-h-9 overflow-hidden' className='group text-light'>
      <div>
        <div tw='absolute z-[10] flex justify-between items-center h-40 w-full px-4 pt-6 block bottom-0 from-dark to-transparent bg-gradient-to-t'>
          <div tw='flex flex-col'>
            <Title tw='flex-grow-0 line-clamp-1' marginBottom={false}>
              {item.title}
            </Title>
            <TextLine
              tw='line-clamp-1'
              dangerouslySetInnerHTML={{ __html: strippedContent(item.description) }}
            />
          </div>
          <div tw='flex-shrink-0'>
            <KudoAmount numKudos={item.stats.kudos_today} hideActions large />
          </div>
        </div>
        <div tw='absolute z-[1] w-full h-full object-cover object-center'>
          <ItemImage item={item} landscape hideSummary notRounded />
        </div>
      </div>
    </div>
  );
};

export const Item = ({
  item,
  index,
  isTopper,
  isSidebar = false,
  landscape = false,
  useCF = false,
  period,
  page,
  sliderItem,
  playlistItem = null,
  toggleMobilePlayList,
  setToggleMobilePlayList,
  ...restProps
}) => {
  const { user } = useContext(UserContext);
  const { isItemDirect } = useContext(RouterContext);
  const isPlaylistItem = playlistItem && playlistItem.itemId;
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();

  const itemClick = (e) => {
    gaEvent(
      `dmp_${item.media_type === 'VIDEO' ? 'video' : 'image'}_item_click`,
      gaItemEventLabel(item, isSidebar, isTopper),
      'item_click',
    );
    if (e && !(e.ctrlKey || e.metaKey) && !playlistItem && !isItemDirect) {
      e.preventDefault();
      setSearchParams({ selectedId: item.id });
    }
    if (playlistItem && item.composite_id) {
      e.preventDefault();
      if (toggleMobilePlayList) {
        setToggleMobilePlayList(!toggleMobilePlayList);
      }
      navigate(
        `${PLAYLIST_PATH.public}/${playlistItem.currentUserId}/${playlistItem.playlistId}/${playlistItem.itemId}`,
      );
    }
  };

  return (
    <ItemClickWrapper
      landscape={landscape}
      id={item.id}
      onClick={(e) => itemClick(e)}
      sliderItem={sliderItem}
      {...restProps}
    >
      {sliderItem && <SliderItem item={item} />}
      {!sliderItem && (
        <>
          <ItemImage
            item={item}
            index={index}
            isTopper={isTopper}
            landscape={landscape}
            period={period}
            page={page}
            isSidebar={isSidebar}
            isPlaylistItem={isPlaylistItem}
            hideSummary={isPlaylistItem}
            useCF={useCF}
          />
          <div
            css={[
              tw`flex items-center flex-grow mt-0.5`,
              landscape && tw`border-b border-b-icons border-opacity-30 sm:border-none`,
            ]}
          >
            <ItemContent user={user} item={item} landscape={landscape} isSidebar={isSidebar} />
          </div>
        </>
      )}
    </ItemClickWrapper>
  );
};
